import BaseView from "./baseView.js";

const EMPTY_CONTENT_MESSAGE =
  "<p>Seems like there is no content to be shown ...</p>";
const HEADER_OFFSET = 160;
const ACTIVE_BAND_BOTTOM = "55%";
const OBSERVER_ROOT_MARGIN = `-${HEADER_OFFSET}px 0px -${ACTIVE_BAND_BOTTOM} 0px`;
const BLOCK_SELECTOR = "[data-id]";
const BLOCK_HEADING_SELECTOR = ":scope > h1, :scope > h2";

const collectObservedTargets = (sectionElement) => {
  const targets = [];
  const blocks = sectionElement.querySelectorAll(BLOCK_SELECTOR);

  blocks.forEach((block) => {
    const heading = block.querySelector(BLOCK_HEADING_SELECTOR);
    if (heading) {
      targets.push(heading);
      return;
    }
    targets.push(block);
  });
  return targets;
};
const GITHUB_CALENDAR_SCRIPT_ID = "github-calendar-script";
const GITHUB_CALENDAR_SCRIPT_URL =
  "https://unpkg.com/github-calendar@latest/dist/github-calendar.min.js";
const GITHUB_USERNAME = "MatthiasSw";

class SectionView extends BaseView {
  intersectionObservers = [];

  constructor() {
    super("main");
  }

  #clearContent = (sectionName) => {
    const sectionElement = document.getElementById(sectionName);
    if (sectionElement) {
      sectionElement.innerHTML = "";
    }
  };

  generateSectionsHTML = (names) => {
    const sectionsHTML = names.map(
      (name) => `<section id="${name}" data-id="${name}"></section>`
    );
    return sectionsHTML.join("");
  };

  renderContent = (sectionName, sectionHTMLContent) => {
    const sectionElement = document.getElementById(sectionName);
    if (sectionElement === null) {
      console.warn(`Section with id "${sectionName}" not found.`);
      return;
    }

    this.#clearContent(sectionName);

    let contentToRender = EMPTY_CONTENT_MESSAGE;
    const hasContent = Boolean(sectionHTMLContent?.trim());
    if (hasContent) {
      contentToRender = sectionHTMLContent;
    }

    sectionElement.insertAdjacentHTML("afterbegin", contentToRender);
    Prism.highlightAllUnder(sectionElement);
  };

  renderGithubCalendar = () => {
    const isScriptAlreadyAdded = Boolean(
      document.getElementById(GITHUB_CALENDAR_SCRIPT_ID)
    );
    if (isScriptAlreadyAdded) {
      return;
    }

    const githubCalendarScript = document.createElement("script");
    githubCalendarScript.id = GITHUB_CALENDAR_SCRIPT_ID;
    githubCalendarScript.src = GITHUB_CALENDAR_SCRIPT_URL;
    githubCalendarScript.onload = () => {
      GitHubCalendar("#github-calendar", GITHUB_USERNAME);
    };
    document.body.appendChild(githubCalendarScript);
  };

  setActiveSection = (sectionName) => {
    const currentActiveSection = document.querySelector("section.active");
    if (currentActiveSection) {
      currentActiveSection.classList.remove("active");
    }

    const newActiveSection = document.getElementById(sectionName);
    if (newActiveSection) {
      newActiveSection.classList.add("active");
      return;
    }
    console.warn(`Section with id "${sectionName}" not found.`);
  };

  hideAllSections = () => {
    const sections = document.querySelectorAll("section");
    sections.forEach((section) => {
      section.classList.remove("active");
      section.classList.add("hidden");
    });
  };

  unhideSection = (sectionName) => {
    const section = document.getElementById(sectionName);
    if (section) {
      section.classList.add("active");
      section.classList.remove("hidden");
    }
  };

  showSection = (sectionName) => {
    this.hideAllSections();
    this.unhideSection(sectionName);
  };

  scrollTo = (dataId) => {
    const target = document.querySelector(`main [data-id="${dataId}"]`);
    const container = document.querySelector("main");
    if (target && container) {
      container.scrollTo({
        top: target.offsetTop - HEADER_OFFSET,
        behavior: "smooth",
      });
    }
  };

  cleanup = () => {
    this.intersectionObservers.forEach((observer) => observer.disconnect());
    this.intersectionObservers = [];
  };

  addIntersectionObserver = (handler, sectionName) => {
    const sectionElement = document.getElementById(sectionName);
    if (sectionElement === null) {
      console.error(`Section with id "${sectionName}" not found.`);
      return;
    }

    let lastReportedTarget = null;

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        const hasEnteredActiveBand =
          entry.isIntersecting && entry.target !== lastReportedTarget;
        if (hasEnteredActiveBand) {
          lastReportedTarget = entry.target;
          const block = entry.target.closest(BLOCK_SELECTOR);
          handler(block.dataset.id);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: 0,
      rootMargin: OBSERVER_ROOT_MARGIN,
    });
    this.intersectionObservers.push(observer);

    const observedTargets = collectObservedTargets(sectionElement);
    observedTargets.forEach((target) => observer.observe(target));
  };
}

export default new SectionView();
